/**
 * 商务风尚模版 - 页脚
 *
 * 设计理念：商务深色底 + 香槟金细线
 * - 深墨绿黑底，金色顶线
 * - 4栏网格：品牌区 + 快捷导航 + 更多服务 + 联系方式
 * - 方角图标容器 + 金色 hover，商务正式感
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function ExecutiveFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
    { id: "m7", label: "隐私政策", href: "/page/privacy", external: false, enabled: true },
    { id: "m8", label: "服务协议", href: "/page/register-agreement", external: false, enabled: true },
  ];

  const enableVip = siteConfig?.enable_vip ?? true;
  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = (hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus)
    .filter((m: any) => m.href !== '/vip' || enableVip);

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `\u00a9 ${currentYear} ${copyrightOwner}` : `\u00a9 ${currentYear}`;

  const brandName = displayName || siteName;

  const quickMenus = menus.slice(0, 4);
  const moreMenus = menus.slice(4, 8);

  return (
    <footer className="bg-[#0F241B] text-gray-400">
      {/* 金色顶线 */}
      <div className="h-[3px] bg-gradient-to-r from-[#C2A04B] via-[#DDC583] to-[#C2A04B]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* ── 顶部品牌横幅 ── */}
        <div className="mb-12 pb-10 border-b border-white/10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt={brandName} className="h-9 w-9 object-contain rounded-md" />
              ) : (
                <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white text-xs font-bold shadow-md border border-[#DDC583]/40">
                  {brandName ? brandName.charAt(0) : 'E'}
                </div>
              )}
              <div>
                {brandName && <span className="text-xl font-bold text-white">{brandName}</span>}
                <p className="text-xs text-gray-500 mt-0.5">
                  {footerInfo || '分类信息综合服务平台，助力信息高效流通'}
                </p>
              </div>
            </div>
            {/* 联系方式图标 */}
            <div className="flex items-center gap-3">
              {phone && (
                <a href={`tel:${phone}`} className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center border border-[#C2A04B]/30 hover:bg-[#C2A04B]/20 hover:text-[#DDC583] transition-colors" title="电话联系">
                  <i className="ri-phone-line text-lg" />
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center border border-[#C2A04B]/30 hover:bg-[#C2A04B]/20 hover:text-[#DDC583] transition-colors" title="发送邮件">
                  <i className="ri-mail-line text-lg" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── 4栏网格 ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* 栏目1: 品牌简介 */}
          <div className="lg:col-span-1">
            <h4 className="text-sm font-semibold text-[#DDC583] mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#C2A04B] rounded-full inline-block" />
              关于平台
            </h4>
            <p className="text-sm leading-relaxed text-gray-500">
              {footerInfo || '分类信息综合服务平台，汇聚各类商业与生活服务信息，为商家与用户搭建高效可靠的信息桥梁。'}
            </p>
          </div>

          {/* 栏目2: 快捷导航 */}
          <div>
            <h4 className="text-sm font-semibold text-[#DDC583] mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#C2A04B] rounded-full inline-block" />
              快捷导航
            </h4>
            <ul className="space-y-3">
              {quickMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-[#DDC583] transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-[#DDC583] transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 栏目3: 更多服务 */}
          <div>
            <h4 className="text-sm font-semibold text-[#DDC583] mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#C2A04B] rounded-full inline-block" />
              更多服务
            </h4>
            <ul className="space-y-3">
              {moreMenus.length > 0 ? moreMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-[#DDC583] transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-[#DDC583] transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              )) : (
                <li className="text-sm text-gray-500">暂无更多</li>
              )}
            </ul>
          </div>

          {/* 栏目4: 联系方式 */}
          <div>
            <h4 className="text-sm font-semibold text-[#DDC583] mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-[#C2A04B] rounded-full inline-block" />
              联系我们
            </h4>
            <ul className="space-y-3">
              {phone && (
                <li>
                  <a href={`tel:${phone}`}
                    className="flex items-center gap-3 text-sm hover:text-[#DDC583] transition-colors group">
                    <span className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-[#C2A04B]/20 transition-colors shrink-0 border border-white/10">
                      <i className="ri-phone-line text-[#DDC583]" />
                    </span>
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`}
                    className="flex items-center gap-3 text-sm hover:text-[#DDC583] transition-colors group">
                    <span className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center group-hover:bg-[#C2A04B]/20 transition-colors shrink-0 border border-white/10">
                      <i className="ri-mail-line text-[#DDC583]" />
                    </span>
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li>
                  <div className="flex items-start gap-3 text-sm group">
                    <span className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center shrink-0 mt-0.5 border border-white/10">
                      <i className="ri-map-pin-line text-[#DDC583]" />
                    </span>
                    <span>{address}</span>
                  </div>
                </li>
              )}
              {!phone && !email && !address && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{copyright}</span>
          <div className="flex items-center gap-4">
            {icp && (
              <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer"
                className="text-xs text-gray-500 hover:text-[#DDC583] transition-colors">
                {icp}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
